import React from 'react';
import './HowToUse.css';

export interface HowToStep {
  number: string;
  title: string;
  description: string;
  details: string[];
  icon: string;
}

export const steps: HowToStep[] = [
  {
    number: '01',
    title: 'Paste Your JSON Data',
    description: 'Copy JSON from any source—API responses, database queries, configuration files, or any structured data. Paste it directly into the editor.',
    details: [
      'Supports nested objects and arrays',
      'Works with any valid JSON structure',
      'Handles files up to 10MB',
      'Real-time syntax validation'
    ],
    icon: '📝'
  },
  {
    number: '02',
    title: 'Convert to TOON Format',
    description: 'Click the convert button and watch as your JSON transforms into compact TOON format. The conversion is instant and happens entirely in your browser.',
    details: [
      'Lightning-fast conversion',
      'Zero data loss or corruption',
      'Maintains all data types',
      'Preserves nested structures'
    ],
    icon: '⚡'
  },
  {
    number: '03',
    title: 'Review Token Savings',
    description: 'See exactly how many tokens you\'re saving with detailed statistics. Compare JSON vs TOON token counts and calculate your potential cost savings.',
    details: [
      'Real-time token counting',
      'Percentage reduction display',
      'Estimated cost savings',
      'Side-by-side comparison'
    ],
    icon: '📊'
  },
  {
    number: '04',
    title: 'Copy and Integrate',
    description: 'Use your TOON formatted data immediately. Copy to clipboard, download as file, or integrate into your LLM prompts, API calls, and data pipelines.',
    details: [
      'One-click copy to clipboard',
      'Download as .toon file',
      'Use in any LLM prompt',
      'Integrate into your workflow'
    ],
    icon: '🚀'
  }
];

const HowToUse: React.FC = () => {
  const useCases = [
    {
      title: 'RAG Systems',
      description: 'Reduce token usage in retrieval-augmented generation by up to 60%',
      icon: '🔍'
    },
    {
      title: 'API Integration',
      description: 'Optimize API responses before sending to LLMs for processing',
      icon: '🔌'
    },
    {
      title: 'Chatbots',
      description: 'Lower costs in conversational AI by compressing context data',
      icon: '💬'
    },
    {
      title: 'Data Analysis',
      description: 'Send more data to LLMs within token limits for better insights',
      icon: '📈'
    },
    {
      title: 'Code Generation',
      description: 'Include larger configuration files in code generation prompts',
      icon: '💻'
    },
    {
      title: 'Workflow Optimization',
      description: 'Streamline your data processing workflows with efficient TOON format',
      icon: '⚙️'
    }
  ];

  return (
    <section className="howto-section" id="how-to-use" aria-labelledby="howto-heading">
      <div className="howto-container">
        <header className="howto-header">
          <h2 id="howto-heading" className="howto-title">
            How to Use JSON to TOON Converter
          </h2>
          <p className="howto-subtitle">
            Start optimizing your LLM token usage in 4 simple steps
          </p>
        </header>

        <div className="steps-container">
          {steps.map((step, index) => (
            <article key={index} className="step-card">
              <div className="step-number" aria-label={`Step ${step.number}`}>
                {step.number}
              </div>
              <div className="step-content">
                <div className="step-header">
                  <span className="step-icon" aria-hidden="true">{step.icon}</span>
                  <h3 className="step-title">{step.title}</h3>
                </div>
                <p className="step-description">{step.description}</p>
                <ul className="step-details">
                  {step.details.map((detail, idx) => (
                    <li key={idx}>
                      <span className="detail-check" aria-hidden="true">✓</span>
                      {detail}
                    </li>
                  ))}
                </ul>
              </div>
            </article>
          ))}
        </div>

        <div className="usecases-section">
          <h3 className="usecases-title">Popular Use Cases</h3>
          <p className="usecases-subtitle">
            See how developers are using TOON to optimize their AI applications
          </p>
          <div className="usecases-grid">
            {useCases.map((useCase, index) => (
              <div key={index} className="usecase-card">
                <span className="usecase-icon" aria-hidden="true">{useCase.icon}</span>
                <h4 className="usecase-title">{useCase.title}</h4>
                <p className="usecase-description">{useCase.description}</p>
              </div>
            ))}
          </div>
        </div>

      </div>
    </section>
  );
};

export default HowToUse;
